import { useQuery } from '@tanstack/react-query';
import { Alert, Button, buttonClass, Card, EmptyState, PageTitle, Spinner } from '@rooming/ui';
import { formatDateRangeInZone, formatInZone } from '@rooming/web-core';
import { Link } from 'react-router';
import { passengerApi, type MyStay } from '../api';
import { messageOf } from '../messages';

const STATUS_TEXT: Record<MyStay['status'], string> = {
  DRAFT: 'En preparación',
  OPEN: 'Selección abierta',
  CONFIRMED: 'Distribución confirmada',
  REOPENED: 'Reabierta',
  CLOSED: 'Cerrada',
};

/** "Mis estadías": the stays of the viewer's group, with dates in the HOTEL's time zone (DR-30). */
export function StaysPage() {
  const stays = useQuery({ queryKey: ['my-stays'], queryFn: async () => (await passengerApi.myStays()).data.items });

  if (stays.isPending) return <Spinner />;
  if (stays.isError)
    return (
      <Alert tone="error" title="No pudimos cargar tus estadías">
        <p>{messageOf(stays.error)}</p>
        <Button variant="secondary" className="mt-3" onClick={() => void stays.refetch()}>
          Reintentar
        </Button>
      </Alert>
    );

  return (
    <>
      <PageTitle subtitle="Elegí una estadía para ver las habitaciones.">Mis estadías</PageTitle>
      {stays.data.length === 0 ? (
        <EmptyState title="Todavía no tenés estadías">Cuando tu coordinador las cargue, aparecen acá.</EmptyState>
      ) : (
        <ul className="grid gap-4 sm:grid-cols-2">
          {stays.data.map((stay) => {
            const zone = stay.hotel?.timezone ?? 'UTC';
            return (
              <li key={stay.id}>
                <Card className="flex h-full flex-col gap-2">
                  <h2 className="text-lg font-semibold break-words">{stay.hotel?.name ?? 'Hotel a confirmar'}</h2>
                  {stay.hotel !== null && <p className="text-sm text-muted">{stay.hotel.city}</p>}
                  <p className="text-base">{formatDateRangeInZone(stay.checkInAt, stay.checkOutAt, zone)}</p>
                  <p className="text-sm font-medium text-brand-700">{STATUS_TEXT[stay.status]}</p>
                  {stay.selectionClosesAt !== null && stay.status === 'OPEN' && (
                    <p className="text-sm text-muted">
                      Podés elegir hasta el {formatInZone(stay.selectionClosesAt, zone)} (hora del hotel)
                    </p>
                  )}
                  <Link to={`/estadias/${stay.id}`} className={buttonClass('primary', 'mt-auto')}>
                    Ver habitaciones
                  </Link>
                </Card>
              </li>
            );
          })}
        </ul>
      )}
    </>
  );
}
